/**
 * Floor Plan Service
 *
 * Handles floor plan upload, annotation upload, generation calls,
 * and fetching existing floor plan data.
 */

import { supabase, STORAGE_BUCKET } from '@/lib/supabase';
import { DbImage, FloorplanFlooring, FloorplanTextHandling, FloorplanGenerationMode, FloorplanConfig, FloorplanRoomLabel, AreaFlooring, FloorplanTransform, FloorplanArrow } from '@/types';
import type { FloorplanVersion, FloorplanMessage, BoxCoordinates, FloorplanAnalyzeResult, FloorplanRefinementPreview } from '@/types';
import type { IntentClassification } from '@/types/floorplanClassification';

// ============================================================================
// Types
// ============================================================================

interface GenerateFloorplanResponse {
  success: boolean;
  imageUrl?: string;
  storagePath?: string;
  imageId?: string;
  error?: string;
  code?: string;
}

// Pricing v4: returned by the generate-floorplan Edge Function (HTTP 402) when a
// Grundriss-Einheit would be consumed but none is available.
export const INSUFFICIENT_FLOORPLAN_UNITS_CODE = 'INSUFFICIENT_FLOORPLAN_UNITS';
const INSUFFICIENT_FLOORPLAN_UNITS_MESSAGE =
  'Kein Grundriss-Kontingent verfügbar. Buche das Grundriss-Paket dazu oder kaufe einen Einzel-Grundriss (15 €).';

// ============================================================================
// Upload Functions
// ============================================================================

/**
 * Upload a floor plan file to Supabase Storage and create a DB record.
 * Returns the database ID, storage path, and preview URL.
 */
export const uploadFloorplan = async (
  projectId: string,
  file: File
): Promise<{ dbId: string; storagePath: string; previewUrl: string }> => {
  console.log(`[Floorplan] Uploading ${file.name} (${file.size} bytes)...`);

  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
  const storagePath = `${projectId}/${timestamp}-${random}-floorplan-original.${ext}`;

  // Upload to storage
  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, file, {
      contentType: file.type || 'image/png',
      cacheControl: '3600',
    });

  if (uploadError) {
    throw new Error(`Upload fehlgeschlagen: ${uploadError.message}`);
  }

  // Insert DB record with room_type='floorplan' as sentinel
  // D.2: mirror storage_path into source_storage_path so unit-extraction can later
  // overwrite storage_path without losing the immutable original reference.
  const { data: imageRow, error: dbError } = await supabase
    .from('images')
    .insert({
      project_id: projectId,
      storage_path: storagePath,
      source_storage_path: storagePath,
      room_type: 'floorplan',
      keep_kitchen: false,
      status: 'pending',
    })
    .select('id')
    .single();

  if (dbError || !imageRow) {
    // Rollback storage upload on DB failure
    await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
    throw new Error(`Datenbankfehler: ${dbError?.message || 'Kein Datensatz erstellt'}`);
  }

  // Get public URL
  const { data: urlData } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(storagePath);

  console.log(`[Floorplan] Uploaded: ${storagePath}, DB ID: ${imageRow.id}`);

  return {
    dbId: imageRow.id,
    storagePath,
    previewUrl: urlData.publicUrl,
  };
};

/**
 * Upload an annotated canvas image (labels burned in, or repair dots/zones)
 * to Supabase Storage. Returns the storage path.
 */
export const uploadAnnotatedImage = async (
  projectId: string,
  imageDbId: string,
  canvas: HTMLCanvasElement
): Promise<string> => {
  console.log(`[Floorplan] Uploading annotated image for ${imageDbId}...`);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
      'image/png'
    );
  });

  const timestamp = Date.now();
  const storagePath = `${projectId}/${timestamp}_${imageDbId.substring(0, 8)}_annotated.png`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, blob, {
      contentType: 'image/png',
      cacheControl: '3600',
      upsert: true,
    });

  if (uploadError) {
    throw new Error(`Annotiertes Bild Upload fehlgeschlagen: ${uploadError.message}`);
  }

  console.log(`[Floorplan] Annotated image uploaded: ${storagePath}`);
  return storagePath;
};

/**
 * Upload a pre-rendered fixture-annotated image blob to Storage.
 * Returns the storage path.
 */
export const uploadAnnotatedBlob = async (
  projectId: string,
  imageDbId: string,
  blob: Blob
): Promise<string> => {
  console.log(`[Floorplan] Uploading fixture-annotated blob for ${imageDbId}...`);

  const timestamp = Date.now();
  const storagePath = `${projectId}/${timestamp}_${imageDbId.substring(0, 8)}_fixtures.png`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, blob, {
      contentType: 'image/png',
      cacheControl: '3600',
      upsert: true,
    });

  if (uploadError) {
    throw new Error(`Fixture-annotiertes Bild Upload fehlgeschlagen: ${uploadError.message}`);
  }

  console.log(`[Floorplan] Fixture-annotated image uploaded: ${storagePath}`);
  return storagePath;
};

// ============================================================================
// Generation
// ============================================================================

/**
 * Call the generate-floorplan Edge Function.
 */
export const generateFloorplan = async (
  mode: FloorplanGenerationMode,
  imagePath: string,
  projectId: string,
  imageId?: string,
  userId?: string,
  config?: { flooring: FloorplanFlooring; textHandling: FloorplanTextHandling; roomLabels?: FloorplanRoomLabel[]; roomsLabeled?: boolean; areaFlooring?: AreaFlooring; fixtureTypes?: string[] },
  originalImagePath?: string,
  finalPromptOverride?: string
): Promise<GenerateFloorplanResponse> => {
  console.log(`[Floorplan] Calling Edge Function: mode=${mode}, imagePath=${imagePath}`);

  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke<GenerateFloorplanResponse>(
    'generate-floorplan',
    {
      body: {
        mode,
        imagePath,
        originalImagePath,
        projectId,
        imageId,
        userId,
        config,
        finalPromptOverride,
      },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    }
  );

  if (error) {
    console.error('[Floorplan] Edge Function error:', error);
    let errorMessage = error.message || 'Unbekannter Fehler';
    let errorCode: string | undefined;
    try {
      if (error.context && typeof error.context.json === 'function') {
        const errorBody = await error.context.json();
        errorMessage = errorBody?.error || errorMessage;
        errorCode = typeof errorBody?.code === 'string' ? errorBody.code : undefined;
      }
    } catch {
      // Context already consumed
    }
    // Pricing v4: surface the quota message as-is so views can show it directly
    // and point the user to buying floorplan quota (no technical prefix).
    if (errorCode === INSUFFICIENT_FLOORPLAN_UNITS_CODE) {
      throw new Error(errorMessage || INSUFFICIENT_FLOORPLAN_UNITS_MESSAGE);
    }
    throw new Error(`Grundriss-Generierung fehlgeschlagen: ${errorMessage}`);
  }

  if (!data || !data.success) {
    if (data?.code === INSUFFICIENT_FLOORPLAN_UNITS_CODE) {
      throw new Error(data.error || INSUFFICIENT_FLOORPLAN_UNITS_MESSAGE);
    }
    const errorMsg = data?.error || 'Unbekannter Fehler';
    console.error('[Floorplan] Generation failed:', errorMsg);
    throw new Error(errorMsg);
  }

  console.log(`[Floorplan] Successfully generated ${mode} image`);
  return data;
};

/**
 * Admin-only analyst preview: runs the vision-analyst on a 2D floorplan
 * without generating an image (previewOnly). Returns the fixture plan and
 * the assembled/effective prompts for inspection.
 */
export const analyzeFloorplan2D = async (
  imagePath: string,
  projectId: string,
  opts: { consultSystemPromptOverride?: string; fixtureBlockTemplateOverride?: string },
): Promise<FloorplanAnalyzeResult> => {
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke(
    'generate-floorplan',
    {
      body: {
        mode: '2d_generation',
        imagePath,
        projectId,
        previewOnly: true,
        consultSystemPromptOverride: opts.consultSystemPromptOverride,
        fixtureBlockTemplateOverride: opts.fixtureBlockTemplateOverride,
      },
      headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
    }
  );

  if (error) {
    console.error('[Floorplan] Analyze Edge Function error:', error);
    let errorMessage = error.message || 'Unbekannter Fehler';
    try {
      if (error.context && typeof error.context.json === 'function') {
        const errorBody = await error.context.json();
        errorMessage = errorBody?.error || errorMessage;
      }
    } catch {
      // Context already consumed
    }
    throw new Error(errorMessage);
  }

  if (!data?.success) {
    throw new Error(data?.error ?? 'Analyse fehlgeschlagen');
  }

  return {
    fixturePlan: data.fixturePlan ?? null,
    assembledPrompt: data.assembledPrompt ?? '',
    effectiveConsultPrompt: data.effectiveConsultPrompt ?? '',
    effectiveBlockTemplate: data.effectiveBlockTemplate ?? '',
  };
};

// ============================================================================
// Fetch Existing
// ============================================================================

/**
 * Fetch the existing floor plan image record for a project.
 * Returns the newest floorplan image, or null if none exists.
 */
export const fetchFloorplanImage = async (projectId: string): Promise<DbImage | null> => {
  const { data, error } = await supabase
    .from('images')
    .select('*')
    .eq('project_id', projectId)
    .eq('room_type', 'floorplan')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[Floorplan] Failed to fetch floorplan image:', error.message);
    return null;
  }

  return data as DbImage | null;
};

/**
 * Save floorplan config to the database.
 */
export const saveFloorplanConfig = async (
  imageId: string,
  config: FloorplanConfig
): Promise<void> => {
  const { error } = await supabase
    .from('images')
    .update({ floorplan_config: config })
    .eq('id', imageId);

  if (error) {
    console.error('[Floorplan] Failed to save config:', error.message);
  }
};

// ============================================================================
// Phase E — Branding transform persistence
// ============================================================================

export async function saveFloorplanTransform(
  imageId: string,
  variant: '2d' | '3d',
  transform: FloorplanTransform
): Promise<void> {
  const column = variant === '2d' ? 'floorplan_2d_transform' : 'floorplan_3d_transform';
  const { error } = await supabase
    .from('images')
    .update({ [column]: transform })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Transform konnte nicht gespeichert werden: ${error.message}`);
  }
}

export async function saveFloorplanArrow(
  imageId: string,
  variant: '2d' | '3d',
  arrow: FloorplanArrow | null
): Promise<void> {
  const column = variant === '2d' ? 'floorplan_2d_entry_arrow' : 'floorplan_3d_entry_arrow';
  const { error } = await supabase
    .from('images')
    .update({ [column]: arrow })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Pfeil konnte nicht gespeichert werden: ${error.message}`);
  }
}

export async function uploadFloorplanComposite(
  projectId: string,
  imageId: string,
  variant: '2d' | '3d',
  blob: Blob
): Promise<string> {
  const storagePath = `projects/${projectId}/floorplan_${variant}_branded/${imageId}-${Date.now()}.png`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, blob, { contentType: 'image/png', upsert: true });

  if (uploadError) {
    throw new Error(`Composite-Upload fehlgeschlagen: ${uploadError.message}`);
  }

  const column = variant === '2d' ? 'storage_path_floorplan_2d_branded' : 'storage_path_floorplan_3d_branded';
  const { error: dbError } = await supabase
    .from('images')
    .update({ [column]: storagePath })
    .eq('id', imageId);

  if (dbError) {
    await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
    throw new Error(`Composite-Referenz konnte nicht gespeichert werden: ${dbError.message}`);
  }

  return storagePath;
}

export async function archivePreviousFloorplan(
  imageId: string,
  variant: '2d' | '3d',
  storagePath: string
): Promise<void> {
  const versionType = variant === '2d' ? 'Floorplan2D' : 'Floorplan3D';
  const { error } = await supabase
    .from('image_history')
    .insert({
      image_id: imageId,
      storage_path: storagePath,
      version_type: versionType,
    });

  if (error) {
    console.warn(`[Floorplan] History-Archiv fehlgeschlagen (${variant}):`, error.message);
  }
}

/**
 * Archive existing 2D + 3D generations to image_history, then clear all
 * floorplan-specific columns on the image row so the user gets a clean slate
 * while retaining the original upload + history for recovery.
 */
export async function archiveAndClearFloorplanState(
  imageId: string,
  generated2dPath: string | null,
  generated3dPath: string | null
): Promise<void> {
  if (generated2dPath) {
    await archivePreviousFloorplan(imageId, '2d', generated2dPath);
  }
  if (generated3dPath) {
    await archivePreviousFloorplan(imageId, '3d', generated3dPath);
  }

  const { error } = await supabase
    .from('images')
    .update({
      storage_path_floorplan_2d: null,
      storage_path_floorplan_3d: null,
      storage_path_floorplan_2d_branded: null,
      storage_path_floorplan_3d_branded: null,
      floorplan_2d_transform: null,
      floorplan_3d_transform: null,
      floorplan_2d_entry_arrow: null,
      floorplan_3d_entry_arrow: null,
      floorplan_config: null,
    })
    .eq('id', imageId);

  if (error) {
    throw new Error(`Konnte Grundriss-Stand nicht zurücksetzen: ${error.message}`);
  }
}

// ============================================================================
// Phase D.2 — Version / Message CRUD for Turn-by-Turn refinement
// ============================================================================

type FloorplanVersionRow = {
  id: string;
  image_id: string;
  version_number: number;
  initial_storage_path: string;
  created_at: string;
};

type FloorplanMessageRow = {
  id: string;
  version_id: string;
  message_number: number;
  user_prompt: string;
  box_coordinates: BoxCoordinates | null;
  resulting_storage_path: string | null;
  openai_response_id: string | null;
  credits_charged: number | string; // numeric from pg can arrive as string
  status: 'pending' | 'success' | 'failed';
  error_message: string | null;
  created_at: string;
};

function mapVersionRow(row: FloorplanVersionRow): FloorplanVersion {
  return {
    id: row.id,
    imageId: row.image_id,
    versionNumber: row.version_number,
    initialStoragePath: row.initial_storage_path,
    createdAt: row.created_at,
  };
}

function mapMessageRow(row: FloorplanMessageRow): FloorplanMessage {
  return {
    id: row.id,
    versionId: row.version_id,
    messageNumber: row.message_number,
    userPrompt: row.user_prompt,
    boxCoordinates: row.box_coordinates,
    resultingStoragePath: row.resulting_storage_path,
    openaiResponseId: row.openai_response_id,
    creditsCharged: Number(row.credits_charged),
    status: row.status,
    errorMessage: row.error_message,
    createdAt: row.created_at,
  };
}

/** Insert a new FloorplanVersion row with auto-incremented version_number. */
export async function createVersion(
  imageId: string,
  initialStoragePath: string
): Promise<FloorplanVersion> {
  const { data: existing, error: existErr } = await supabase
    .from('floorplan_versions')
    .select('version_number')
    .eq('image_id', imageId)
    .order('version_number', { ascending: false })
    .limit(1);
  if (existErr) throw existErr;

  const nextNumber = (existing?.[0]?.version_number ?? 0) + 1;

  const { data, error } = await supabase
    .from('floorplan_versions')
    .insert({
      image_id: imageId,
      version_number: nextNumber,
      initial_storage_path: initialStoragePath,
    })
    .select()
    .single();
  if (error || !data) throw error ?? new Error('createVersion: no row returned');

  return mapVersionRow(data as FloorplanVersionRow);
}

/** Insert a pending FloorplanMessage with auto-incremented message_number. */
export async function createPendingMessage(
  versionId: string,
  userPrompt: string,
  boxCoordinates: BoxCoordinates | null
): Promise<FloorplanMessage> {
  const { data: existing, error: existErr } = await supabase
    .from('floorplan_messages')
    .select('message_number')
    .eq('version_id', versionId)
    .order('message_number', { ascending: false })
    .limit(1);
  if (existErr) throw existErr;

  const nextNumber = (existing?.[0]?.message_number ?? 0) + 1;

  const { data, error } = await supabase
    .from('floorplan_messages')
    .insert({
      version_id: versionId,
      message_number: nextNumber,
      user_prompt: userPrompt,
      box_coordinates: boxCoordinates,
      status: 'pending',
      credits_charged: 0,
    })
    .select()
    .single();
  if (error || !data) throw error ?? new Error('createPendingMessage: no row returned');

  return mapMessageRow(data as FloorplanMessageRow);
}

/** Fetch all versions for an image, plus their messages grouped by version id. */
export async function fetchVersionsWithMessages(imageId: string): Promise<{
  versions: FloorplanVersion[];
  messagesByVersion: Record<string, FloorplanMessage[]>;
}> {
  const { data: vData, error: vErr } = await supabase
    .from('floorplan_versions')
    .select('*')
    .eq('image_id', imageId)
    .order('version_number');
  if (vErr) throw vErr;

  const versions = (vData ?? []).map((r) => mapVersionRow(r as FloorplanVersionRow));
  if (versions.length === 0) return { versions: [], messagesByVersion: {} };

  const versionIds = versions.map((v) => v.id);
  const { data: mData, error: mErr } = await supabase
    .from('floorplan_messages')
    .select('*')
    .in('version_id', versionIds)
    .order('message_number');
  if (mErr) throw mErr;

  const messagesByVersion: Record<string, FloorplanMessage[]> = {};
  for (const row of mData ?? []) {
    const msg = mapMessageRow(row as FloorplanMessageRow);
    if (!messagesByVersion[msg.versionId]) messagesByVersion[msg.versionId] = [];
    messagesByVersion[msg.versionId].push(msg);
  }

  return { versions, messagesByVersion };
}

/**
 * Hard-reset: archive + delete all messages in a version with message_number > keepUpTo.
 * Archived message images go to image_history (type='Floorplan2D').
 */
export async function hardResetFromMessage(
  versionId: string,
  keepUpToMessageNumber: number
): Promise<void> {
  // Fetch soon-to-be-deleted messages with their image paths
  const { data: toArchive, error: fetchErr } = await supabase
    .from('floorplan_messages')
    .select('resulting_storage_path, version_id, message_number, status')
    .eq('version_id', versionId)
    .gt('message_number', keepUpToMessageNumber)
    .eq('status', 'success');
  if (fetchErr) throw fetchErr;

  if (toArchive && toArchive.length > 0) {
    // Resolve image_id from the version
    const { data: versionRow, error: versionErr } = await supabase
      .from('floorplan_versions')
      .select('image_id')
      .eq('id', versionId)
      .single();
    if (versionErr) throw versionErr;

    if (versionRow?.image_id) {
      const historyRows = toArchive
        .filter((r) => r.resulting_storage_path)
        .map((r) => ({
          image_id: versionRow.image_id,
          storage_path: r.resulting_storage_path as string,
          version_type: 'Floorplan2D',
        }));

      if (historyRows.length > 0) {
        const { error: histErr } = await supabase.from('image_history').insert(historyRows);
        if (histErr) {
          console.warn('[floorplanService] hardReset: archive-to-history failed:', histErr.message);
          // Continue with delete even if archive fails — user's reset intent takes precedence
        }
      }
    }
  }

  const { error: delErr } = await supabase
    .from('floorplan_messages')
    .delete()
    .eq('version_id', versionId)
    .gt('message_number', keepUpToMessageNumber);
  if (delErr) throw delErr;
}

/** Update images.active_version_id + active_message_number pointers. */
export async function setActivePointer(
  imageId: string,
  activeVersionId: string | null,
  activeMessageNumber: number | null
): Promise<void> {
  const { error } = await supabase
    .from('images')
    .update({
      active_version_id: activeVersionId,
      active_message_number: activeMessageNumber,
    })
    .eq('id', imageId);
  if (error) throw error;
}

// ============================================================================
// Phase 2 — Rebuild Trigger
// ============================================================================

/**
 * Trigger a full new V<n+1> floorplan generation as the user-confirmed
 * response to a `rebuild_suggested` modal. Calls the existing
 * `generate-floorplan` edge function with the source storage path.
 */
export async function triggerFloorplanRebuild(args: {
  imageId: string;
  projectId: string;
  userId: string;
  sourceStoragePath: string;
  classification: IntentClassification;
}): Promise<{ newVersionId: string; newVersionNumber: number }> {
  const { data, error } = await supabase.functions.invoke('generate-floorplan', {
    body: {
      imageId: args.imageId,
      projectId: args.projectId,
      userId: args.userId,
      sourceStoragePath: args.sourceStoragePath,
      reason: 'rebuild_after_classifier_suggestion',
      classifierContext: args.classification,
    },
  });
  if (error) throw new Error(`triggerFloorplanRebuild: ${error.message}`);
  if (!data?.newVersionId || typeof data.newVersionNumber !== 'number') {
    throw new Error('triggerFloorplanRebuild: malformed response');
  }
  return { newVersionId: data.newVersionId, newVersionNumber: data.newVersionNumber };
}

// ============================================================================
// Refine v2 — Admin prompt-inspector preview
// ============================================================================

/**
 * Admin-only dry-run: classify + assemble the final refinement prompt and
 * return the routing decision WITHOUT generating an image (no credit, no
 * image-model call). Mirrors `requestStageRefinementPreview` in
 * geminiService.ts; non-admin callers get a 403 from the edge function.
 */
export async function requestFloorplanRefinementPreview(params: {
  imageId: string;
  versionId: string;
  messageId: string;
  userPrompt: string;
  boxCoordinates?: BoxCoordinates;
  currentStatePath: string;
  sourceStoragePath: string;
  projectId: string;
  userId: string;
  classifierPromptOverride?: string;
  templateOverride?: string;
  templateOverrideKey?: string;
  finalPromptOverride?: string;
  editTypeOverride?: string;
  scopeOverride?: 'local' | 'global';
}): Promise<FloorplanRefinementPreview> {
  const { data: { session } } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('generate-floorplan-refinement', {
    body: { ...params, previewOnly: true },
    headers: session ? { Authorization: `Bearer ${session.access_token}` } : undefined,
  });

  if (error) {
    console.error('[floorplanService.requestFloorplanRefinementPreview] error', error);
    let errorMessage = error.message || 'Analyse fehlgeschlagen';
    try {
      if (error.context && typeof error.context.json === 'function') {
        const errorBody = await error.context.json();
        errorMessage = errorBody?.errorMessage || errorBody?.error || errorMessage;
      }
    } catch {
      // Context already consumed
    }
    throw new Error(errorMessage);
  }
  if (!data || (data as { errorMessage?: string }).errorMessage) {
    throw new Error((data as { errorMessage?: string })?.errorMessage ?? 'Analyse fehlgeschlagen');
  }
  return data as FloorplanRefinementPreview;
}
